import { Space, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Shared clickable token palette. Fixed tokens render as blue chips (labelled
 * from the `customTemplateTokens.*` i18n namespace); ad-hoc custom-field keys
 * render as green chips (the "new chip" the author defines themselves).
 *
 * Two behaviours by context:
 *   - `onInsert` given (builder editor): clicking inserts the token into the doc.
 *   - `onInsert` omitted (DOCX upload / legacy reference): clicking copies the
 *     raw `{token}` to the clipboard, since you cannot insert into a Word file.
 *
 * @param {Array<{token:string}>} tokens          - Fixed DB-backed tokens.
 * @param {string[]}              customFieldKeys  - Ad-hoc field keys.
 * @param {(raw:string)=>void}    [onInsert]       - Insert handler; enables insert mode.
 */
export default function TokenChips({
  tokens = [],
  customFieldKeys = [],
  onInsert = null,
}) {
  const { t } = useTranslation('forms');

  const act = (raw) => {
    if (onInsert) {
      onInsert(raw);
      return;
    }
    const text = `{${raw}}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    message.success(t('tokenCopied', 'Kopirano: {{token}}', { token: text }));
  };

  return (
    <Space size={[4, 4]} wrap>
      {tokens.map(({ token }) => (
        <Tag
          key={token}
          color="blue"
          title={`{${token}}`}
          style={{ cursor: 'pointer' }}
          onClick={() => act(token)}
        >
          {t(`customTemplateTokens.${token}`, token)}
        </Tag>
      ))}
      {customFieldKeys.map((key) => (
        <Tag
          key={key}
          color="green"
          title={`{${key}}`}
          style={{ cursor: 'pointer' }}
          onClick={() => act(key)}
        >
          {key}
        </Tag>
      ))}
    </Space>
  );
}
